import { createGlobalStyle } from 'styled-components';
import '@fontsource/dm-sans/500.css';
import '@fontsource/dm-sans/700.css';
import '@fontsource/playfair-display';
import '@fontsource/playfair-display/400-italic.css';
import '@fontsource/playfair-display/700-italic.css';

const GlobalStyle = createGlobalStyle`
  html {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      font-size: ${props => props.theme.fluidType( 0 )};
      *,
      *::before,
      *::after { box-sizing: border-box; }
  }

  body {
      margin: 0;
      // Use system fonts: https://css-tricks.com/snippets/css/system-font-stack/
      font-family: "Playfair Display", sans-serif;
      color: ${props => props.theme.colours.black};
      background-color: ${props => props.theme.colours.white};
  }
  
  .sans { 
    font-family: "DM Sans", sans-serif;
    font-weight: 500;
  }

  h1, h2, h3, h4, h5, h6,
  .h1, .h2, .h3, .h4, .h5, .h6 {
    font-style: italic;
    font-weight: 400;
    line-height: 1.8;
    margin-top: 1rem;
    margin-bottom: 1rem;
    a { text-decoration: none; }
    a:hover { text-decoration: none; }
  }
  
  p,
  ol, ul, li,
  code, kbd, pre, samp {
      line-height: 2;
      margin-top: 1rem;
      margin-bottom: 1rem;
  }

  h1, .h1 { ${props => props.theme.fluidType( 2 )}; }
  h2, .h2 { ${props => props.theme.fluidType( 2 )}; }
  h3, .h3 { ${props => props.theme.fluidType( 2 )}; }
  h4, .h4 { ${props => props.theme.fluidType( 1 )}; }
  h5, .h5 { ${props => props.theme.fluidType( 0 )}; }
  h6, .h6 { ${props => props.theme.fluidType( -1 )}; }

  li {
      margin-top: 0;
      margin-bottom: 0;
  }
  
  .smallcaps { 
    font-family: "DM Sans", sans-serif;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1rem;
    ${props => props.theme.fluidType( -1 )};
  }
  
  .gatsby-image-wrapper {
    background-color: ${props => props.theme.colours.lightGrey};
  }

  small, p.small { ${props => props.theme.fluidType( -1 )}; }
  
  code, kbd, pre, samp {
      font-family: monospace;
      white-space: normal;
  }
  
  ul {
      padding-left: 1rem;
      list-style-type: disc;
  }
  
  ol {
      padding-left: 2rem;
      list-style-type: decimal;
  }
  
  video {
    width: 100%;
    height: auto;
    margin-bottom: 1rem;
  }
  
  em, i { font-style: italic; }
  
  strong, b { font-weight: bold; }
  
  blockquote {
      font-weight: normal;
      padding: 0;
      margin: 4rem 0;
      font-style: italic;
      p:first-child {
       ${props => props.theme.fluidType( 1 )};
      }
      > :first-child { margin-top: 0; }
      > :last-child { margin-bottom: 0; }
  }
  
  a { 
    color: inherit;
    text-decoration: none;
  }
  
  sup, sub {
      vertical-align: baseline;
      position: relative;
      top: -0.4em;
  }
  
  sub { top: 0.4em; }
  
  // Forms
  
  .two-col-fields {
    @media(${props => props.theme.breakpoints.sm}) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 1rem;
    }
  }
  
  label {
      ${props => props.theme.fluidType( 0 )};
      font-style: italic;
      line-height: 1.2;
      font-weight: normal;
      &.single-checkbox {
        display: block;
        margin: 1rem 0;
      }
  }
  
  .field-holder {
    margin-bottom: 2rem;
    label { display: none; }
  }
  
  .submit-holder { margin-top: 3rem; }
  
  .text-input,
  input[type="text"],
  input[type="password"],
  input[type="date"],
  input[type="datetime-local"],
  input[type="email"],
  input[type="month"],
  input[type="number"],
  input[type="range"],
  input[type="search"],
  input[type="tel"],
  input[type="time"],
  input[type="url"],
  input[type="week"],
  textarea {
      display: block;
      font-family:inherit;
      font-style: italic;
      ${props => props.theme.fluidType( 0 )};
      color: inherit;
      padding: 0.75rem 1rem;
      border: 1px solid;
      border-radius: 0;
      line-height: 1.5rem;
      background-color: transparent;
      -webkit-appearance: none;
      width: 100%;
      &.small {
        padding: 0.25rem 0.5rem;
      }
  }
  
  ::placeholder { 
    color: ${props => props.theme.colours.black};
    opacity: 1;
  }
  
  .file-input,
  input[type="file"],
  .radio-input,
  input[type="radio"],
  .checkbox-input,
  input[type="checkbox"] {
      font-family:inherit;
  }
  
  // Select
  
  select {
    // A reset of styles, including removing the default dropdown arrow
    appearance: none;
    // Additional resets for further consistency
    background-color: transparent;
    border: none;
    padding: 0.75rem 0.5rem;
    margin: 0;
    width: 100%;
    font-family: inherit;
    font-size: inherit;
    cursor: inherit;
    line-height: inherit;
    outline: none;
    color: ${props => props.theme.colours.black};
    font-style: italic;
  }
  
  select::-ms-expand {
    display: none;
  }
  
  .select {
    width: 100%;
    border: 1px solid;
    border-radius: 0;
    padding: 0.25rem 0.5rem;
    ${props => props.theme.fluidType( 0 )};
    cursor: pointer;
    line-height: 1.1;
    background-color: transparent;
    grid-template-areas: "select";
    display: grid;
    align-items: center;
    &:after {
      content: "";
      width: 0.8rem;
      height: 0.5rem;
      background-color: ${props => props.theme.colours.black};
      clip-path: polygon(100% 0%, 0 0%, 50% 100%);
      justify-self: end;
    }
    select,
    &:after {
      grid-area: select;
    }
  }
  
  // Checkbox
  
  .checkbox {
    display: block;
    position: relative;
    cursor: pointer;
    user-select: none;
    padding-top: 0.75rem;
    margin-bottom: 2rem;
    // Hide the browser's default radio button
    input {
        position: absolute;
        opacity: 0;
        z-index: -1;
    }
    .checkmark {
        position: absolute;
        top: 0.75rem;
        left: 0;
        height: 1.6rem;
        width: 1.6rem;
        border: 0.1rem solid ${props => props.theme.colours.black};
        border-radius: 50%;
        background-color: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        :after {
          display: none;
          content: '';
          width: 1rem;
          height: 1rem;
          border-radius: 50%;
          background-color: ${props => props.theme.colours.black};
        }
    }
    .label {
        display: block;
        line-height: 1.6rem;
        margin: 0;
        position: relative;
        padding-left: 3rem;
    }
    // On hover
    // &:hover > input ~ .checkmark {
    //     border: 1px solid ${props => props.theme.colours.focus};
    // }
    input:checked ~ .checkmark:after {
        display: block;
    }
  }
  
  .button,
  button,
  input[type="submit"],
  input[type="button"],
  input[type="reset"] {
      display: inline-block;
  
      padding: 0.75rem 1.5rem;
      margin: 0;
  
      ${props => props.theme.fluidType( -1 )};
      line-height: 1;
      text-decoration: none;
      white-space: nowrap;
      border: 1px solid;
      border-radius: 0;
  
      color: inherit;
      background-color: transparent;
      text-transform: uppercase;
      
      font-family: "DM Sans", sans-serif;
      font-weight: bold;
      letter-spacing: 0.1rem;
      
      transition: all 0.25s;
      cursor: pointer;
      @media(${props => props.theme.breakpoints.md}) {
        ${props => props.theme.fluidType( -1 )};
      }
      
      &:hover {
        text-decoration: none;
        color: ${props => props.theme.colours.white};
        background-color: ${props => props.theme.colours.black};
        border-color: ${props => props.theme.colours.black};
      }
      
      &.dark {
        color: ${props => props.theme.colours.white};
        background-color: ${props => props.theme.colours.black};
        border-color: ${props => props.theme.colours.black};
      }
      &.dark:hover {
        color: ${props => props.theme.colours.black};
        background-color: transparent;
      }
      
      &.inverse {
        color: ${props => props.theme.colours.white};
      }
      &.inverse:hover {
        color: ${props => props.theme.colours.black};
        background-color: ${props => props.theme.colours.white};
        border-color: ${props => props.theme.colours.white};
      }
      
      &.icon {
        padding: 0;
        border: none;
        border-radius: 0;
        line-height: 0;
        svg {
          width: 1.2rem;
          height: auto;
          display: block;
        }
      }
      &.icon:hover {
        color: inherit;
        background-color: transparent;
      }
      &.icon.small {
        svg {
          width: 0.75rem;
        }
      }
  }
  
  .errorMsg {
      color: ${props => props.theme.colours.error};
  }
`;

export default GlobalStyle;
